import { flatMap, isEmpty } from 'lodash';
import type { AppId } from '@cld-remote/application_wrapper/index';
import { AssetVisibility, UnicornApi } from '@cld/unicorn-api';
import { ProductListDataSchema } from '../api/productListData';
import { isAllowedForOnboarding, shouldRedirectToOnboarding } from './onboarding';

const CLOUDINARY_ADMINISTRATIVE_CLOUD_NAMES = ['demo', 'cloudinary', 'product-demos', 'prod'];

type Permissions = UnicornApi['data']['permissions'];
type User = UnicornApi['data']['user'];
type Role = User['role'];
type ProductEnvironment = UnicornApi['data']['productEnvironment'];
type Capabilities = UnicornApi['data']['capabilities'];

interface GetFeaturesOptions {
  unicornApiData: UnicornApi['data'];
  productList: ProductListDataSchema;
  cloudinaryAssetsAppId: AppId;
}

const ADMIN_ROLE = 'admin';
const MASTER_ADMIN_ROLE = 'masterAdmin';
const TECHNICAL_ADMIN_ROLE = 'technicalAdmin';

const isAllowedForGettingStartedPage = (role?: Role) => role && [MASTER_ADMIN_ROLE, ADMIN_ROLE, TECHNICAL_ADMIN_ROLE].includes(role);

const isDAMUserBasedOnUserRole = (role?: Role) => role && (role === 'mediaLibraryAdmin' || role === 'mediaLibraryUser');

const isAllowedForDashboard = (permissions: Permissions, role?: Role) => {
  if (isDAMUserBasedOnUserRole(role)) {
    return false;
  }
  return (
    // flag on
    permissions.includes('administrateMl') ||
    // flag off (all roles are covered)
    permissions.includes('readImages') ||
    permissions.includes('viewReports')
  );
};

const isAllowedForReports = ({ permissions, user }: { user: User; permissions: Permissions }) => {
  const isLoggedInAsSelf = !user.impersonationActive;
  return isLoggedInAsSelf ? permissions.includes('viewReports') : permissions.includes('seeAccountReports');
};

const isDamDisabled = (productList: ProductListDataSchema) => {
  if (isEmpty(productList)) {
    return false;
  }
  const flatProducts = flatMap(productList.products);
  const damApps = ['digital_asset_management', 'cloudinary_assets'];
  return !flatProducts.some((app) => damApps.includes(app?.id));
};

const isAllowedForMediaLibrary = (
  { permissions, capabilities, user }: { capabilities: Capabilities; user: User; permissions: Permissions },
  productList: ProductListDataSchema
) => {
  if (isDamDisabled(productList)) {
    return false;
  }
  const isMediaLibraryPermissionsEnabled = capabilities.includes('mediaLibraryPermissions');
  const isLoggedInAsSelf = !user.impersonationActive;

  return isLoggedInAsSelf
    ? isMediaLibraryPermissionsEnabled
      ? permissions.includes('useMl')
      : permissions.includes('readImages') || permissions.includes('contribute') || permissions.includes('manageImages')
    : permissions.includes('seeAccountImages');
};

const isAllowedForMediaExplorer = ({ user }: { user: User }) => {
  return user.role !== 'reports' && user.role !== 'billing';
};

const isAllowedForVideoPages = ({ user }: { user: User }) => {
  return user.role !== 'reports' && user.role !== 'billing';
};

const isAllowedForNewVideoApp = ({ capabilities }: { capabilities: Capabilities }) => {
  return capabilities.includes('newVideoApp') ?? false;
};

const isAllowedToCreateUsers = (role?: string) => role && [MASTER_ADMIN_ROLE, ADMIN_ROLE].includes(role);

const shouldDisplayThumbnails = ({ assetVisibility, user, customer }: { assetVisibility: AssetVisibility; customer: ProductEnvironment; user: User }) => {
  // User is logged in using the "login as" flow
  const userIsImpersonated = user.impersonationActive;

  // If the user is not impersonated, always show thumbnails
  if (!userIsImpersonated) return true;

  const userIsCloudinaryAdmin = user.isCloudinaryAdminEmployee;
  const isCloudinaryCloud = CLOUDINARY_ADMINISTRATIVE_CLOUD_NAMES.includes(customer.cloudName);

  // Admins can always see assets on 'demo' and 'cloudinary' clouds
  if (userIsCloudinaryAdmin && isCloudinaryCloud) return true;

  // If the user is impersonated, show thumbnails only if asset visibility was explicitly enabled
  return assetVisibility === 'visible';
};

// TODO: This could ideally come from new endpoint of from BFF once we remove login_from_session call
export const getFeatures = ({
  unicornApiData: { assetVisibility, capabilities, permissions, productEnvironment: customer, user, plan, deprecatedData, account },
  productList,
  cloudinaryAssetsAppId,
}: GetFeaturesOptions) => {
  return {
    isAllowedForDashboard: isAllowedForDashboard(permissions, user.role),
    isAllowedForGettingStartedPage: isAllowedForGettingStartedPage(user.role),
    isAllowedForTransformations: permissions.includes('manageTransformations'),
    isAllowedForReports: isAllowedForReports({ permissions, user }),
    isAllowedForMediaLibrary: isAllowedForMediaLibrary({ capabilities, permissions, user }, productList),
    isAllowedForAddons: permissions.includes('modifyAccountSettings'),
    isCmsMode: deprecatedData.cmsMode,
    // TODO: Check if we still need to return this feature
    isAllowedForMediaExplorer: isAllowedForMediaExplorer({ user }),
    isAllowedForVideoPages: isAllowedForVideoPages({ user }),
    isAllowedForNewVideoApp: isAllowedForNewVideoApp({ capabilities }),
    isNewVideoReportsPageEnabled: capabilities.includes('newVideoReportsPage'),
    isNewErrorReportsPageEnabled: capabilities.includes('newErrorReportsPage'),
    isFreeAccount: !plan.paid,
    isAllowedForOnboarding: isAllowedForOnboarding({ permissions, user }),
    shouldRedirectToOnboarding: shouldRedirectToOnboarding({ user_events_latest: deprecatedData.userEvents }),
    isAllowedToCreateUsers: isAllowedToCreateUsers(user.role),
    usedNewDashboardBeforeFullRelease: capabilities.includes('newDeveloperDashboardEnabled'),
    shouldDisplayThumbnails: shouldDisplayThumbnails({ assetVisibility, customer, user }),
    isAllowedForMonthlyValueReport: user.canViewReports,
    shouldDisplayMarketplace: account.newBillingSystem && (user.role === 'masterAdmin' || user.role === 'billing'),
  };
};

export type Features = ReturnType<typeof getFeatures>;

export const isAllowedForProgrammableMedia = (features: Features) => {
  const { isAllowedForDashboard, isAllowedForGettingStartedPage, isAllowedForTransformations, isAllowedForReports, isAllowedForAddons } = features;
  return [isAllowedForDashboard, isAllowedForGettingStartedPage, isAllowedForTransformations, isAllowedForReports, isAllowedForAddons].some(Boolean);
};
